import React, { useState, useEffect, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tooltip,
  styled
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

import SeveritySelector from './buttons_cqlSevereIntern';

import { format, subDays } from 'date-fns';

// Registramos módulos de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

// Estilo personalizado para el tooltip
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#1976d2',
    color: '#fff',
    fontSize: '0.8rem',
    borderRadius: '4px',
    padding: '8px'
  },
  '& .MuiTooltip-arrow': {
    color: '#1976d2'
  }
}));

// ================ Funciones Auxiliares ================ //
const transformForChart = (data, mapLeftLayer) => {
  return data.map(item => ({
    ...item,
    HITS_rel: typeof item.HITS_rel === 'string'
      ? parseFloat((parseFloat(item.HITS_rel.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.HITS_rel * 100).toFixed(1)),
    MISSES_rel: typeof item.MISSES_rel === 'string'
      ? parseFloat((parseFloat(item.MISSES_rel.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.MISSES_rel * 100).toFixed(1)),
    FAR: item.FAR,
    GSS: item.GSS,
    F1: item.F1,
    RECALL: item.RECALL,
    PRECISION: item.PRECISION,
    STORM_FREQUENCY: mapLeftLayer === 'VALIDATION_PLAN_v2:EU_025_historical_view' ? (item.TP_abs + item.FN_abs) / 46529 : mapLeftLayer === 'VALIDATION_PLAN_v2:EU_0125_historical_view' ? (item.TP_abs + item.FN_abs) / 54233 : 0,
    // N1M metrics
    HITS_rel_n1m: typeof item.HITS_rel_n1m === 'string'
      ? parseFloat((parseFloat(item.HITS_rel_n1m.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.HITS_rel_n1m * 100).toFixed(1)),
    MISSES_rel_n1m: typeof item.MISSES_rel_n1m === 'string'
      ? parseFloat((parseFloat(item.MISSES_rel_n1m.replace('%', '')) * 100).toFixed(1))
      : parseFloat((item.MISSES_rel_n1m * 100).toFixed(1)),
    FAR_n1m: item.FAR_n1m,
    GSS_n1m: item.GSS_n1m,
    F1_n1m: item.F1_n1m,
    RECALL_n1m: item.RECALL_n1m,
    PRECISION_n1m: item.PRECISION_n1m,
    STORM_FREQUENCY_n1m: mapLeftLayer === 'VALIDATION_PLAN_v2:EU_025_historical_view' ? (item.TP_abs_n1m + item.FN_abs_n1m) / 46529 : mapLeftLayer === 'VALIDATION_PLAN_v2:EU_0125_historical_view' ? (item.TP_abs_n1m + item.FN_abs_n1m) / 54233 : 0,
  }));
};

const pivotChartData = (data, metricKey) => {
  const fixedHours = [];
  for (let h = 0; h < 24; h++) {
    fixedHours.push({ hour: h });
  }

  const issueTimestampsSet = new Set();
  data.forEach(row => {
    const validDate = new Date(row.VALID_TIMESTAMP);
    const hour = validDate.getUTCHours();
    if (hour >= 0 && hour < 24) {
      const issue = row.ISSUE_TIMESTAMP;
      issueTimestampsSet.add(issue);
      const value = row[metricKey];
      fixedHours[hour][issue] = value;
    }
  });

  const dataset = fixedHours.map(item => ({
    ...item,
    hourLabel: item.hour.toString().padStart(2, '0') + ":00"
  }));

  const colors = [
    '#229386f1', '#1f77b4ff', '#05e3f7ff', '#0000fff7',
    '#efa75fff', '#c0495cff', '#f714b4ff', '#ba4bf1ff',
    '#f5f56fff', '#86861bff', '#b2df8aff', '#2ca02cff',
  ];
  const sortedIssues = Array.from(issueTimestampsSet).sort();
  const customLabels = [
    "D-2 01 UTC", "D-2 07 UTC", "D-2 13 UTC", "D-2 19 UTC",
    "D-1 01 UTC", "D-1 07 UTC", "D-1 13 UTC", "D-1 19 UTC",
    "D-0 01 UTC", "D-0 07 UTC", "D-0 13 UTC", "D-0 19 UTC"
  ];

  const series = sortedIssues.map((issue, index) => ({
    dataKey: issue,
    myLabel: customLabels[index] || `Issue Time: ${issue}`,
    color: colors[index % colors.length],
  }));

  return { dataset, series };
};

const formatAndAddHours = (isoString) => {
  if (!isoString) return 'Unknown Date';
  const date = new Date(isoString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.00`;
};

const calculateYAxisRange = (dataset, marginPercentage = 0.1) => {
  const values = [];
  dataset.forEach(item => {
    Object.keys(item).forEach(key => {
      if (key !== 'hour' && key !== 'hourLabel') {
        const value = item[key];
        if (value !== null && value !== undefined) {
          values.push(value);
        }
      }
    });
  });
  if (values.length === 0) return { min: 0, max: 100 };

  const min = Math.min(...values);
  const max = Math.max(...values);
  const margin = (max - min) * marginPercentage;
  return { min: Math.max(0, min - margin), max: max + margin };
};

function groupSeries(series) {
  const groups = {
    'D-0': [],
    'D-1': [],
    'D-2': [],
    'STORM FREQUENCY': []
  };

  series.forEach((s) => {
    if (s.dataKey.startsWith('storm-')) {
      groups['STORM FREQUENCY'].push(s);
    } else if (s.myLabel.startsWith('D-0')) {
      groups['D-0'].push(s);
    } else if (s.myLabel.startsWith('D-1')) {
      groups['D-1'].push(s);
    } else if (s.myLabel.startsWith('D-2')) {
      groups['D-2'].push(s);
    }
  });

  return groups;
}

function getYAxisConfig(metric) {
  const baseMetric =  metric;

  // Configuración común para métricas porcentuales:
  const percentageConfig = {
    formatter: (val) => `${val.toFixed(1)}%`,
    max: 100, // Fijamos el máximo en 100%
  };

  switch (baseMetric) {
    case 'FAR':
      return {
        label: 'FAR score',
        formatter: (val) => val.toFixed(2),
      };
    case 'FAR_n1m':
      return {
        label: 'FAR score',
        formatter: (val) => val.toFixed(2),
      };
    case 'GSS':
      return {
        label: 'GSS score',
        formatter: (val) => val.toFixed(2),
      };
    case 'GSS_n1m':
      return {
        label: 'GSS score',
        formatter: (val) => val.toFixed(2),
      };
    case 'F1':
      return {
        label: 'F1 score',
        formatter: (val) => val.toFixed(2),
      };
    case 'F1_n1m':
      return {
        label: 'F1 score',
        formatter: (val) => val.toFixed(2),
      };  
    case 'RECALL':
      return {
        label: 'RECALL score',
        formatter: (val) => val.toFixed(2),
      };
    case 'RECALL_n1m':
      return {
        label: 'RECALL score',
        formatter: (val) => val.toFixed(2),
      };
    case 'PRECISION':
      return {
        label: 'PRECISION score',
        formatter: (val) => val.toFixed(2),
      };
    case 'PRECISION_n1m':
      return {
        label: 'PRECISION score',
        formatter: (val) => val.toFixed(2),
      };
    case 'HITS_rel':
      return {
        label: 'HITS score (%)',
        ...percentageConfig,
      };
    case 'HITS_rel_n1m':
      return {
        label: 'HITS score (%)',
        ...percentageConfig,
      };
    case 'MISSES_rel':
      return {
        label: 'MISSES score (%)',
        ...percentageConfig,
      };
    case 'MISSES_rel_n1m':
      return {
        label: 'MISSES score (%)',
        ...percentageConfig,
      };
    default:
      return {
        label: 'Metric Value',
        ...percentageConfig,
      };
  }
}

const cqlSevere_formatter = (cqlSevere) => {
  switch(cqlSevere) {
    case 'Light':
      return 'Light';
    case 'Severe':
      return 'Severe';
    case 'Lightning':
      return 'Lightning';
    case "High_Density_Lightning":
      return 'High-density lightning';
    case 'Overshoot':
        return 'Overshoot';
    default:
        return cqlSevere;
  }
};

function TemporalGraph({
  cqlFechaMax,
  cqlFechaMin,
  cqlSevere,
  weekNumber,
  metricsNames,
  issueTime,
  handleRiskClick,
  mapLeftLayer,
}) {
  const [data, setData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('HITS_rel');
  const [error, setError] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [visibleSeries, setVisibleSeries] = useState({});
  const [internalCqlSevere, setInternalCqlSevere] = useState(cqlSevere);

  // Checkboxes
  const [N1Mmetrics, setN1Mmetrics] = useState(false);
  const [showStormFrequencyLine, setShowStormFrequencyLine] = useState(true);

  useEffect(() => {
    const fetchDataAndTransform = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/temporalGraph?` +
          `cqlFechaMax=${encodeURIComponent(cqlFechaMax)}` +
          `&cqlFechaMin=${encodeURIComponent(cqlFechaMin)}` +
          `&cqlSevere=${encodeURIComponent(internalCqlSevere)}` +
          `&weekNumber=${encodeURIComponent(weekNumber)}` +
          `&metricsNames=${encodeURIComponent(metricsNames)}` +
          `&issueTime=${encodeURIComponent(issueTime)}`
        );
        const results = await response.json();
        const sortedResults = results.sort((a, b) => new Date(a.VALID_TIMESTAMP) - new Date(b.VALID_TIMESTAMP));

        if (sortedResults.length > 0) {
          setMinDate(formatAndAddHours(sortedResults[0].VALID_TIMESTAMP));
          setMaxDate(formatAndAddHours(sortedResults[sortedResults.length - 1].VALID_TIMESTAMP));
        } else {
          setMinDate('Unknown Date');
          setMaxDate('Unknown Date');
        }

        const transformed = transformForChart(sortedResults, mapLeftLayer);
        setData(transformed);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Error fetching data');
        setData([]);
      }
    };
    fetchDataAndTransform();
  }, [cqlFechaMax, cqlFechaMin, weekNumber, metricsNames, issueTime, internalCqlSevere, mapLeftLayer]);

  const { dataset: mainDataset, series: mainSeries } = useMemo(() => {
    return pivotChartData(data, selectedMetric);
  }, [data, selectedMetric]);

  const stormMetricKey = N1Mmetrics ? "STORM_FREQUENCY_n1m" : "STORM_FREQUENCY";
  const { dataset: stormDataset, series: stormPivotSeries } = useMemo(() => {
    return pivotChartData(data, stormMetricKey);
  }, [data, stormMetricKey]);

  const rawStormSeries = useMemo(() => {
    return stormPivotSeries.filter(s => s.myLabel === "D-1 19 UTC");
  }, [stormPivotSeries]);

  const annotatedStormSeries = useMemo(() => {
    return rawStormSeries.map(s => ({
      ...s,
      dataKey: `storm-${s.dataKey}`,
      myLabel: `STORM FREQUENCY`,
      color: 'red'
    }));
  }, [rawStormSeries]);

  useEffect(() => {
    const initVisible = {};

    mainSeries.forEach(s => {
      initVisible[s.dataKey] = ["D-0 19 UTC", "D-1 19 UTC", "D-2 19 UTC"].includes(s.myLabel);
    });

    annotatedStormSeries.forEach(s => {
      initVisible[s.dataKey] = true;
    });

    setVisibleSeries(initVisible);
  }, [mainSeries, annotatedStormSeries]);

  const combinedSeries = useMemo(() => {
    const mainVisible = mainSeries.filter(s => visibleSeries[s.dataKey]);
    const stormsVisible = showStormFrequencyLine
      ? annotatedStormSeries.filter(s => visibleSeries[s.dataKey])
      : [];
    return [...mainVisible, ...stormsVisible];
  }, [
    mainSeries,
    annotatedStormSeries,
    visibleSeries,
    showStormFrequencyLine
  ]);

  const mainYAxisRange = useMemo(() => calculateYAxisRange(mainDataset), [mainDataset]);
  const stormYAxisRange = useMemo(() => {
    if (rawStormSeries.length > 0) {
      const key = rawStormSeries[0].dataKey;
      const values = stormDataset.map(d => d[key]).filter(v => v != null);
      if (values.length === 0) return { min: 0, max: 1 };
      const min = Math.min(...values);
      const max = Math.max(...values);
      const margin = (max - min) * 0.1;
      return { min: min - margin, max: max + margin };
    }
    return { min: 0, max: 1 };
  }, [stormDataset, rawStormSeries]);

  const chartData = useMemo(() => {
    const labels = mainDataset.map(d => d.hourLabel);

    const datasets = combinedSeries.map((s) => {
      const isStorm = s.dataKey.startsWith('storm-');
      const sourceDataset = isStorm ? stormDataset : mainDataset;
      const rawKey = isStorm ? s.dataKey.replace('storm-', '') : s.dataKey;

      const dataValues = sourceDataset.map(d =>
        d[rawKey] !== undefined ? d[rawKey] : null
      );

      return {
        label: s.myLabel,
        data: dataValues,
        fill: false,
        borderColor: s.color,
        borderWidth: 2,
        yAxisID: isStorm ? 'yStorm' : 'yMain',
        borderDash: isStorm ? [10, 5] : undefined,
        pointStyle: isStorm ? 'rectRot' : 'circle',
        pointRadius: isStorm ? 2.5 : 3,
        pointHoverRadius: isStorm ? 7 : 5,
        tension: 0.1
      };
    });

    return { labels, datasets };
  }, [mainDataset, stormDataset, combinedSeries]);

  const { label: mainYAxisLabel, formatter: mainTickFormatter } = useMemo(() => {
    return getYAxisConfig(selectedMetric);
  }, [selectedMetric]);

  const chartOptions = useMemo(() => {
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yMain: {
          type: 'linear',
          position: 'left',
          min: mainYAxisRange.min,
          max: mainYAxisRange.max,
          ticks: {
            callback: mainTickFormatter
          },
          title: {
            display: true,
            text: mainYAxisLabel
          }
        },
        x: {
          title: {
            display: true,
            text: `All values for ${selectedMetric.replace('_rel', '').replace('_n1m', '')} metrics (${minDate || 'Unknown Date'} - ${maxDate || 'Unknown Date'} UTC)`
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false
        }
      },
      layout: {
        padding: 10
      }
    };
  
    // Agregamos la escala "yStorm" solo si la línea de Storm está activada
    if (showStormFrequencyLine) {
      const storm = cqlSevere_formatter(cqlSevere);
      options.scales.yStorm = {
        type: 'linear',
        position: 'right',
        color:'red',
        min: 0,
        max: 0.2,
        ticks: {
          callback: (val) => (val * 100).toFixed(0) + '%',
          color: 'red'  // Color de la escala
        },
        title: {
          display: true,
          text: `${storm} Ground Truth Occuppancy (%)`,
          color: 'red',  // Color del título del eje
        },
        grid: {
          drawOnChartArea: false
        }
      };
    }
  
    return options;
  }, [
    mainYAxisRange,
    mainYAxisLabel,
    mainTickFormatter,
    selectedMetric,
    minDate,
    maxDate,
    showStormFrequencyLine
  ]);

  const toggleSeries = (dataKey) => {
    setVisibleSeries(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const baseMetricOptions = [
    { value: 'F1', label: 'F1' },
    { value: 'RECALL', label: 'RECALL' },
    { value: 'PRECISION', label: 'PRECISION' },
    { value: 'FAR', label: 'FAR' },
    { value: 'HITS_rel', label: 'HITS' },
    { value: 'MISSES_rel', label: 'MISSES' },
    { value: 'GSS', label: 'GSS' },
  ];
  const metricOptions = baseMetricOptions.map(option => ({
    value: N1Mmetrics ? `${option.value}_n1m` : option.value,
    label: option.label
  }));

  useEffect(() => {
    if (N1Mmetrics) {
      setSelectedMetric(prev => (!prev.endsWith('_n1m') ? `${prev}_n1m` : prev));
    } else {
      setSelectedMetric(prev => (prev.endsWith('_n1m') ? prev.replace('_n1m', '') : prev));
    }
  }, [N1Mmetrics]);

  const fullSeriesForPanel = useMemo(() => {
    return [...mainSeries, ...annotatedStormSeries];
  }, [mainSeries, annotatedStormSeries]);

  const groupedSeries = useMemo(() => {
    return groupSeries(fullSeriesForPanel);
  }, [fullSeriesForPanel]);

  const StormCheckbox = styled(Checkbox)(({ theme }) => ({
    color: 'red',
    '&.Mui-checked': {
      color: 'red'
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 0, 0, 0.1)'
    }
  }));

  //etiquetar las opciones de D-0, D-1 y D-2
  const getAdjustedDate = (groupName) => {
    if (!issueTime) return 'Unknown';
    // Convertir issueTime a objeto Date
    const date = new Date(issueTime);
    // Determinar cuántos días restar según el grupo
    let daysToSubtract = 0;
    if (groupName === 'D-1') {
      daysToSubtract = 1;
    } else if (groupName === 'D-2') {
      daysToSubtract = 2;
    }
    const adjustedDate = subDays(date, daysToSubtract);
    return format(adjustedDate, 'yyyy-MM-dd');
  };  

  return (
    <Box sx={{ width: '94%', margin: 'auto', height: '100%' }}>
      <Paper
        sx={{
          padding: '7.5px',
          borderRadius: '8px',
          boxShadow: '0px 4px 10px rgba(0,0,0,0.1)'
        }}
        elevation={3}
      >
        {/* Título y descripción */}
        <Typography
          variant="body2"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0px 2px 5px rgba(0,0,0,0.1)',
            marginBottom: '16px',
            textAlign: 'center',
            lineHeight: 1.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TimelineIcon
              sx={{
                mr: 1,
                fontSize: '1.8rem',
                color: '#1976d2'
              }}
            />
            <Box component="span" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
              Metrics Graph for {selectedMetric.replace('_n1m', '').replace('_rel', '')}
            </Box>
          </Box>
          <Box>
            This graph shows all the metric values obtained by the AI with respect to the ground truth for the day selected on the ‘Maps’ screen.
          </Box>
          <Box
            component="span"
            sx={{
              display: 'block',
              fontWeight: 'bold',
              fontSize: '0.70rem',
              color: '#0d47a1',
              backgroundColor: '#bbdefb',
              borderRadius: '7.5px',
              padding: '2px 6px',
              mt: 1
            }}
          >
            VALID TIME availables between: {minDate || 'Unknown Date'} - {maxDate || 'Unknown Date'} UTC
          </Box>
        </Typography>

        {/* Controles */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            mb: 3,
            flexWrap: 'wrap'
          }}
        >
          {/* Checkboxes */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={N1Mmetrics}
                  onChange={(e) => setN1Mmetrics(e.target.checked)}
                  sx={{
                    color: '#1976d2',
                    '&.Mui-checked': { color: '#1976d2' }
                  }}
                />
              }
              label="Show Neighbourhood Metrics"
              sx={{ color: '#1976d2', fontWeight: 'bold' }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <StormCheckbox
                  checked={showStormFrequencyLine}
                  onChange={(e) => setShowStormFrequencyLine(e.target.checked)}
                />
              }
              label={`Show ${cqlSevere_formatter(cqlSevere)} storm frequency line`}
              sx={{ color: 'red', fontWeight: 'bold' }}
            />
            <CustomTooltip
              title={
                <span style={{ fontSize: '14px', fontWeight: '400' }}>
                  If the<strong> value of the red line is close to 0</strong>, it means that there were <strong>not enough storms that day to calculate the metrics</strong>.
                </span>
              }
              arrow
              placement="right"
              sx={{
                color: '#fff', 
                fontSize: '12px', 
                padding: '8px', 
                borderRadius: '4px',

              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, ml: -1.5, color: 'red', cursor: 'pointer' }} />
            </CustomTooltip>

            </Box>
          </Box>

          {/* Selector de severidad centrado */}
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', minWidth: '200px' }}>
            <SeveritySelector
              value={internalCqlSevere}
              onChange={(newValue) => {
                setInternalCqlSevere(newValue);
                handleRiskClick(newValue);
              }}
            />
          </Box>

          {/* Selector de métricas */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Select
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
              displayEmpty
              inputProps={{ 'aria-label': 'Metric Select' }}
              sx={{
                width: '132px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                boxShadow: '0px 2px 10px rgba(0,0,0,0.1)',
                '.MuiSelect-select': { padding: '10px', color: '#333', fontWeight: 'bold' },
                '.MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#115293' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                '.MuiSvgIcon-root': { color: '#1976d2' }
              }}
            >
              {metricOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Contenedor principal */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {/* Gráfica */}
          <Box sx={{ flex: 1, minWidth: '300px', height: '500px' }}>
            <Line
              data={chartData}
              options={chartOptions}
              style={{ height: '100%', width: '100%' }}
            />
          </Box>

          {/* Panel lateral de series */}
          <Box sx={{ width: '250px', minWidth: '180px' }}>
            <Accordion defaultExpanded>
              <AccordionDetails sx={{ p: 0 }}>
                {Object.entries(groupedSeries)
                  .filter(([groupName]) => {
                    if (groupName === 'STORM FREQUENCY' && !showStormFrequencyLine || groupName === 'STORM FREQUENCY' && showStormFrequencyLine ) {
                      return false;
                    }
                    return true;
                  })
                  .map(([groupName, seriesList]) => (
                    <Accordion
                      key={groupName}
                      defaultExpanded
                      sx={{
                        boxShadow: 'none',
                        mb: 0,
                        ...(groupName === 'STORM FREQUENCY' && !showStormFrequencyLine && {
                          display: 'none',
                        })
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: '#efefef',
                          py: 0.1,
                          borderRadius: '7.5px',
                          minHeight: '1px',
                          '&.Mui-expanded': { minHeight: 'auto' }
                        }}
                      >
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          textAlign: 'center',
                          width: '100%',
                          fontSize: '0.8rem',
                          color: 'black'
                        }}
                      >
                        {groupName}{['D-0', 'D-1', 'D-2'].includes(groupName) && ` (${getAdjustedDate(groupName)})`}
                      </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer sx={{ maxHeight: '180px', overflowY: 'auto' }}>
                          <Table size="small">
                            <TableBody>
                              {seriesList.map(s => (
                                <TableRow
                                  key={s.dataKey}
                                  hover
                                  onClick={() => toggleSeries(s.dataKey)}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: visibleSeries[s.dataKey]
                                      ? 'rgba(25, 118, 210, 0.1)'
                                      : 'inherit',
                                    borderLeft: visibleSeries[s.dataKey]
                                      ? `7.5px solid ${s.color}`
                                      : '7.5px solid transparent',
                                    transition: 'background-color 0.3s, border-left 0.3s'
                                  }}
                                >
                                  <TableCell sx={{ border: 'none', pl: 1, py: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      {s.myLabel}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    sx={{ border: 'none', pr: 1, py: 0.5 }}
                                    align="center"
                                  >
                                    <Box
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        backgroundColor: s.color,
                                        borderRadius: '50%',
                                        margin: 'auto'
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default TemporalGraph;
